import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Music, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/lib/settings";
import { fileUrl } from "@/lib/api";

const OFF_KEY = "hr_ambient_music_off";
const PLAYED_KEY = "hr_music_played_once";

const getPlayed = () => {
  try { return JSON.parse(sessionStorage.getItem(PLAYED_KEY) || "[]"); } catch { return []; }
};
const markPlayed = (url) => {
  try {
    const p = getPlayed();
    if (!p.includes(url)) sessionStorage.setItem(PLAYED_KEY, JSON.stringify([...p, url]));
  } catch {}
};

export default function AmbientMusic() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const music = settings?.music || {};
  const allTracks = useMemo(() => (music.tracks || []).filter((t) => t.url), [music]);
  const enabled = !!music.enabled && allTracks.length > 0;
  const volume = Math.min(Math.max(Number(music.volume ?? 35), 0), 100) / 100;

  const playlist = useMemo(() => {
    const matches = (p) => (p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(`${p}/`));
    const specific = allTracks.filter((t) => t.scope === "custom" && (t.pages || []).some(matches));
    const globals = allTracks.filter((t) => (t.scope || "all") === "all");
    return specific.length ? specific : globals;
  }, [allTracks, pathname]);

  const audioRef = useRef(null);
  const playlistRef = useRef(playlist);
  playlistRef.current = playlist;
  const currentUrlRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);
  const userOff = useRef(typeof window !== "undefined" && localStorage.getItem(OFF_KEY) === "1");

  const pickNext = (afterEnded) => {
    const pl = playlistRef.current;
    const played = getPlayed();
    const once = pl.filter((t) => t.play_mode === "once" && !played.includes(t.url));
    const loops = pl.filter((t) => (t.play_mode || "loop") !== "once");
    if (once.length) return once[0];
    if (!loops.length) return null;
    if (!afterEnded) return loops[0];
    const idx = loops.findIndex((t) => t.url === currentUrlRef.current);
    return loops[(idx + 1) % loops.length];
  };

  const playTrack = (audio, t) => {
    if (!t) { setPlaying(false); return; }
    currentUrlRef.current = t.url;
    audio.src = fileUrl(t.url);
    audio.play().then(() => setPlaying(true)).catch(() => {});
  };

  useEffect(() => {
    if (!enabled) return;
    const audio = new Audio();
    audio.preload = "auto";
    audio.volume = volume;
    audioRef.current = audio;
    audio.addEventListener("ended", () => {
      const cur = playlistRef.current.find((t) => t.url === currentUrlRef.current);
      if (cur && cur.play_mode === "once") markPlayed(cur.url);
      playTrack(audio, pickNext(true));
    });

    let cleanupGesture = () => {};
    const first = pickNext(false);
    if (!userOff.current && first) {
      currentUrlRef.current = first.url;
      audio.src = fileUrl(first.url);
      audio.play().then(() => setPlaying(true)).catch(() => {
        const start = () => {
          if (!userOff.current) audio.play().then(() => setPlaying(true)).catch(() => {});
          cleanupGesture();
        };
        window.addEventListener("pointerdown", start, { once: true });
        window.addEventListener("keydown", start, { once: true });
        cleanupGesture = () => {
          window.removeEventListener("pointerdown", start);
          window.removeEventListener("keydown", start);
        };
      });
    }
    return () => {
      cleanupGesture();
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Audio ducking: soften ambient music while a video with sound is playing
  const duckCount = useRef(0);
  useEffect(() => {
    const apply = () => {
      const a = audioRef.current;
      if (a) a.volume = duckCount.current > 0 ? volume * 0.15 : volume;
    };
    const onPlay = () => { duckCount.current += 1; apply(); };
    const onStop = () => { duckCount.current = Math.max(0, duckCount.current - 1); apply(); };
    window.addEventListener("hr-media-play", onPlay);
    window.addEventListener("hr-media-stop", onStop);
    return () => {
      window.removeEventListener("hr-media-play", onPlay);
      window.removeEventListener("hr-media-stop", onStop);
    };
  }, [volume]);

  // On navigation: switch if current track doesn't belong here, or an unplayed voice-over awaits
  const playlistKey = playlist.map((t) => `${t.url}:${t.play_mode || "loop"}`).join("|");
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!playlist.length) {
      audio.pause();
      setPlaying(false);
      return;
    }
    const next = pickNext(false);
    const currentInPlaylist = playlist.some((t) => t.url === currentUrlRef.current);
    const voiceOverWaiting = next && next.play_mode === "once" && next.url !== currentUrlRef.current;
    if (!currentInPlaylist || voiceOverWaiting) {
      if (!userOff.current) playTrack(audio, next);
      else { audio.pause(); setPlaying(false); currentUrlRef.current = next ? next.url : null; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistKey]);

  if (!enabled || playlist.length === 0) return null;

  const toggle = (on) => {
    userOff.current = !on;
    if (on) {
      localStorage.removeItem(OFF_KEY);
      const a = audioRef.current;
      if (!a) return;
      if (a.src && !a.ended) a.play().then(() => setPlaying(true)).catch(() => {});
      else playTrack(a, pickNext(false) || playlistRef.current[0]);
    } else {
      localStorage.setItem(OFF_KEY, "1");
      audioRef.current?.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="fixed bottom-5 left-5 z-[60]" data-testid="ambient-music">
      {open && (
        <div className="absolute bottom-16 left-0 w-[300px] bg-ivory border border-ink/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)] p-5 calc-enter" data-testid="ambient-music-popup">
          <div className="flex items-start justify-between gap-3">
            <div className="overline accent-color">A little music for the journey</div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-graphite hover:text-ink" data-testid="ambient-music-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[13px] text-graphite leading-relaxed mt-3">
            We know searching for the perfect venue isn&apos;t easy. If you&apos;ve found your way here, the least we can do is make the search feel pleasant. This soft melody is a small effort from our side — because we care about how you feel, not just what you book.
          </p>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-ink/10">
            <span className="text-xs uppercase tracking-[0.16em] text-graphite">{playing ? "Playing softly" : "Prefer silence? Anytime."}</span>
            <Switch checked={playing} onCheckedChange={toggle} data-testid="ambient-music-toggle" />
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Ambient music"
        className="w-12 h-12 rounded-full bg-ink text-ivory shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        data-testid="ambient-music-btn"
      >
        {playing ? (
          <span className="flex items-end gap-[3px] h-4">
            <span className="eq-bar w-[3px] bg-ivory rounded-sm" style={{ animationDelay: "0s" }} />
            <span className="eq-bar w-[3px] bg-ivory rounded-sm" style={{ animationDelay: "0.25s" }} />
            <span className="eq-bar w-[3px] bg-ivory rounded-sm" style={{ animationDelay: "0.5s" }} />
          </span>
        ) : (
          <Music className="w-5 h-5 opacity-70" />
        )}
      </button>
    </div>
  );
}
